import { useEffect, useMemo, useState } from 'react'
import { Icon } from '@iconify/react'
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { useAuth } from '@/hooks/useAuth'
import { useMetaConversionsLayout } from '@/layouts/MetaConversionsLayout'
import { formatCompactDate, formatCurrency, formatNumber } from '@/lib/campaign-dashboard'
import { fetchGa4ConversionsDashboard } from '@/lib/google-conversions'
import { fetchMetaConversionsDashboard } from '@/lib/meta-conversions'
import type {
  MetaConversionsDashboardPayload,
  MetaConversionsFunnelStep,
  MetaConversionsUtmRow,
} from '@/types/meta-conversions'
import type {
  Ga4ConversionsDashboardPayload,
  Ga4ConversionsFunnelStep,
  Ga4ConversionsSeriesPoint,
  Ga4ConversionsUtmRow,
} from '@/types/google-conversions'
import { buildDefaultMetaDateRange } from './dateRange'
import styles from './MetaConversionsPage.module.css'

export type MetaConversionsDashboardChannel = 'meta' | 'ga4'

type KpiItem = {
  label: string
  value: string
  icon: string
  subtext: string
}

function formatPercent(value: number): string {
  return `${value.toFixed(2).replace('.', ',')}%`
}

function formatChartLabel(value: string): string {
  const date = new Date(`${value}T12:00:00`)
  if (Number.isNaN(date.getTime())) return value
  return `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}`
}

function buildMetaCapiKpis(data: MetaConversionsDashboardPayload | null): KpiItem[] {
  if (!data) return []

  const { summary } = data

  return [
    {
      label: 'Meta: eventos enviados (CAPI)',
      value: formatNumber(summary.capi_sent),
      icon: 'mdi:send-check-outline',
      subtext: `Taxa de sucesso: ${formatPercent(summary.capi_success_rate_percent)}`,
    },
    {
      label: 'Meta: falhas CAPI',
      value: formatNumber(summary.capi_failed),
      icon: 'mdi:alert-circle-outline',
      subtext: `Taxa de falha: ${formatPercent(summary.capi_failure_rate_percent)}`,
    },
    {
      label: 'Meta: eventos ignorados',
      value: formatNumber(summary.capi_skipped),
      icon: 'mdi:minus-circle-outline',
      subtext: 'Não enviados por falta de token ou regra interna',
    },
    {
      label: 'Receita confirmada (visão Meta)',
      value: formatCurrency(summary.confirmed_revenue),
      icon: 'mdi:cash-multiple',
      subtext: `Cobertura envio CAPI: ${formatPercent(summary.capi_send_rate_percent)}`,
    },
  ]
}

function buildGa4MpKpis(data: Ga4ConversionsDashboardPayload | null): KpiItem[] {
  if (!data) return []

  const { summary } = data

  return [
    {
      label: 'GA4 MP: compras transmitidas',
      value: formatNumber(summary.mp_sent),
      icon: 'mdi:google-analytics',
      subtext: `Taxa de sucesso: ${formatPercent(summary.mp_success_rate_percent)}`,
    },
    {
      label: 'GA4 MP: falhas na transmissão',
      value: formatNumber(summary.mp_failed),
      icon: 'mdi:alert-circle-outline',
      subtext: `Taxa de falha: ${formatPercent(summary.mp_failure_rate_percent)}`,
    },
    {
      label: 'GA4 MP: eventos ignorados',
      value: formatNumber(summary.mp_skipped),
      icon: 'mdi:minus-circle-outline',
      subtext: 'Ignorados sem ID G- ou segredo MP configurados para a campanha',
    },
    {
      label: 'Receita confirmada (visão GA4 MP)',
      value: formatCurrency(summary.confirmed_revenue),
      icon: 'mdi:cash-register',
      subtext: `Cobertura envio Measurement Protocol: ${formatPercent(summary.mp_send_rate_percent)}`,
    },
  ]
}

interface MergedConversionsChartPoint {
  date: string
  page_views: number
  checkouts: number
  purchases: number
  capi_sent?: number
  capi_failed?: number
  mp_sent?: number
  mp_failed?: number
}

function mergeSeriesChartPoints(
  metaPoints: MetaConversionsDashboardPayload['series']['points'] | undefined,
  gaPoints: Ga4ConversionsSeriesPoint[] | undefined
): MergedConversionsChartPoint[] {
  const map = new Map<
    string,
    {
      date: string
      page_views: number
      checkouts: number
      purchases: number
      capi_sent?: number
      capi_failed?: number
      mp_sent?: number
      mp_failed?: number
    }
  >()

  for (const p of metaPoints ?? []) {
    map.set(p.date, {
      date: p.date,
      page_views: p.page_views,
      checkouts: p.checkouts,
      purchases: p.purchases,
      capi_sent: p.capi_sent,
      capi_failed: p.capi_failed,
    })
  }

  for (const p of gaPoints ?? []) {
    const existing = map.get(p.date) ?? {
      date: p.date,
      page_views: p.page_views,
      checkouts: p.checkouts,
      purchases: p.purchases,
    }

    existing.page_views = Math.max(existing.page_views, p.page_views)
    existing.checkouts = Math.max(existing.checkouts, p.checkouts)
    existing.purchases = Math.max(existing.purchases, p.purchases)
    existing.mp_sent = p.mp_sent
    existing.mp_failed = p.mp_failed
    map.set(p.date, existing)
  }

  return Array.from(map.values()).sort((a, b) => a.date.localeCompare(b.date))
}

function renderUtmTable(
  title: string,
  rows: MetaConversionsUtmRow[] | Ga4ConversionsUtmRow[],
  subtitle: string,
  deliveryLabel: string
) {
  return (
    <div className={styles.panel}>
      <div className={styles.utmHeader}>
        <h3 className={styles.panelTitle}>{title}</h3>
        <p className={styles.panelSubtitle}>{subtitle}</p>
      </div>

      <div className={styles.tableScroll} role="region" aria-label={title} tabIndex={0}>
        <table className={styles.utmTable}>
          <thead>
            <tr>
              <th scope="col">Valor</th>
              <th scope="col">Compras</th>
              <th scope="col">Receita</th>
              <th scope="col">{deliveryLabel}</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={4} className={styles.emptyTable}>
                  Nenhum dado no período.
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr key={row.value}>
                  <th scope="row" className={styles.utmValue}>
                    {row.value}
                  </th>
                  <td>{formatNumber(row.purchases)}</td>
                  <td>{formatCurrency(row.revenue)}</td>
                  <td>{formatNumber('capi_sent' in row ? row.capi_sent : row.mp_sent)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function renderFunnelRow(step: MetaConversionsFunnelStep | Ga4ConversionsFunnelStep, base: number) {
  const widthPercent = base > 0 ? Math.max((step.count / base) * 100, 2) : 0

  return (
    <div key={step.key} className={styles.funnelRow}>
      <span className={styles.funnelLabel}>{step.label}</span>
      <div className={styles.funnelBarTrack}>
        <div className={styles.funnelBarFill} style={{ width: `${widthPercent}%` }} />
      </div>
      <div className={styles.funnelRowStats}>
        <span className={styles.funnelCount}>{formatNumber(step.count)}</span>
        <span className={styles.funnelRate}>
          {step.conversion_from_previous_percent === null
            ? 'Base inicial'
            : `${formatPercent(step.conversion_from_previous_percent)} da etapa anterior`}
        </span>
      </div>
    </div>
  )
}

interface MetaConversionsDashboardPageProps {
  channel: MetaConversionsDashboardChannel
}

export function MetaConversionsDashboardPage({ channel }: MetaConversionsDashboardPageProps) {
  const { token } = useAuth()
  const {
    campaignId,
    campaignsLoading,
    hasCampaigns,
    isAdminWithoutInstitution,
  } = useMetaConversionsLayout()

  const defaultRange = useMemo(() => buildDefaultMetaDateRange(), [])
  const [dateFrom, setDateFrom] = useState(defaultRange.dateFrom)
  const [dateTo, setDateTo] = useState(defaultRange.dateTo)
  const [metaData, setMetaData] = useState<MetaConversionsDashboardPayload | null>(null)
  const [ga4Data, setGa4Data] = useState<Ga4ConversionsDashboardPayload | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [metaError, setMetaError] = useState<string | null>(null)
  const [ga4Error, setGa4Error] = useState<string | null>(null)

  useEffect(() => {
    if (!campaignId) {
      setMetaData(null)
      setGa4Data(null)
      setIsLoading(false)
      setMetaError(null)
      setGa4Error(null)
      return
    }

    let cancelled = false

    const load = async () => {
      setIsLoading(true)

      if (channel === 'meta') {
        setGa4Data(null)
        setGa4Error(null)
        setMetaError(null)
        try {
          const payload = await fetchMetaConversionsDashboard(
            campaignId,
            { dateFrom, dateTo },
            token ?? undefined
          )
          if (!cancelled) {
            setMetaData(payload)
          }
        } catch (err) {
          if (!cancelled) {
            setMetaData(null)
            setMetaError(
              err instanceof Error
                ? err.message
                : 'Não foi possível carregar o painel Meta (CAPI).'
            )
          }
        } finally {
          if (!cancelled) {
            setIsLoading(false)
          }
        }
        return
      }

      setMetaData(null)
      setMetaError(null)
      setGa4Error(null)
      try {
        const payload = await fetchGa4ConversionsDashboard(
          campaignId,
          { dateFrom, dateTo },
          token ?? undefined
        )
        if (!cancelled) {
          setGa4Data(payload)
        }
      } catch (err) {
        if (!cancelled) {
          setGa4Data(null)
          setGa4Error(
            err instanceof Error
              ? err.message
              : 'Não foi possível carregar o painel Google GA4 (Measurement Protocol).'
          )
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false)
        }
      }
    }

    void load()

    return () => {
      cancelled = true
    }
  }, [campaignId, channel, dateFrom, dateTo, token])

  const metaCapiKpis = useMemo(() => buildMetaCapiKpis(metaData), [metaData])
  const ga4MpKpis = useMemo(() => buildGa4MpKpis(ga4Data), [ga4Data])

  const chartPoints = useMemo(() => {
    if (channel === 'meta') {
      return mergeSeriesChartPoints(metaData?.series.points, undefined)
    }
    return mergeSeriesChartPoints(undefined, ga4Data?.series.points)
  }, [channel, metaData, ga4Data])

  const funnelBaseMeta = metaData?.funnel?.[0]?.count ?? 0
  const funnelBaseGa = ga4Data?.funnel?.[0]?.count ?? 0

  if (isAdminWithoutInstitution && !campaignsLoading) {
    return (
      <div className={styles.centeredState}>
        <Icon icon="mdi:office-building-outline" className={styles.stateIcon} />
        Selecione uma instituição no topo para visualizar as conversões.
      </div>
    )
  }

  if (!campaignsLoading && !hasCampaigns) {
    return (
      <div className={styles.centeredState}>
        <Icon icon="mdi:bullhorn-outline" className={styles.stateIcon} />
        Nenhuma campanha cadastrada. Crie uma campanha para acompanhar as conversões.
      </div>
    )
  }

  if (campaignId == null) {
    return (
      <div className={styles.centeredState}>
        <Icon icon="mdi:loading" className={styles.spinner} />
        Carregando campanha...
      </div>
    )
  }

  return (
    <div className={styles.page}>
      <div className={styles.filtersCard}>
        <div className={styles.field}>
          <label className={styles.fieldLabel} htmlFor="meta-dashboard-date-from">
            Data inicial
          </label>
          <input
            id="meta-dashboard-date-from"
            type="date"
            className={styles.input}
            value={dateFrom}
            onChange={(event) => setDateFrom(event.target.value)}
          />
        </div>
        <div className={styles.field}>
          <label className={styles.fieldLabel} htmlFor="meta-dashboard-date-to">
            Data final
          </label>
          <input
            id="meta-dashboard-date-to"
            type="date"
            className={styles.input}
            value={dateTo}
            onChange={(event) => setDateTo(event.target.value)}
          />
        </div>
        <p className={styles.helperText}>
          {channel === 'meta'
            ? 'Todas as contagens abaixo são desta campanha. O funil mostra o tráfego na landing até compras; os indicadores e entregas seguintes referem-se somente à Meta Conversions API (CAPI).'
            : 'Todas as contagens abaixo são desta campanha. O funil mostra o tráfego na landing até compras; os indicadores e entregas seguintes referem-se somente ao Measurement Protocol do GA4 (MP).'}
        </p>
      </div>

      {!isLoading && (channel === 'meta' ? metaError : ga4Error) ? (
        <section className={styles.panel} style={{ marginBottom: 'var(--spacing-lg)', borderLeft: '4px solid #f59e0b' }}>
          <h2 className={styles.panelTitle}>Avisos de carregamento</h2>
          {channel === 'meta' && metaError ? (
            <p className={styles.panelSubtitle}>{`Meta CAPI — ${metaError}`}</p>
          ) : null}
          {channel === 'ga4' && ga4Error ? (
            <p className={styles.panelSubtitle}>{`Google GA4 (MP) — ${ga4Error}`}</p>
          ) : null}
        </section>
      ) : null}

      {isLoading ? (
        <div className={styles.centeredState}>
          <Icon icon="mdi:loading" className={styles.spinner} />
          Carregando dashboard...
        </div>
      ) : (channel === 'meta' ? !metaData : !ga4Data) ? (
        <div className={styles.centeredState}>
          <Icon icon="mdi:alert-circle-outline" className={styles.stateIcon} />
          Não foi possível carregar os relatórios. Verifique as mensagens acima e tente outro período.
        </div>
      ) : (
        <>
          <div className={styles.kpiChannelIntro}>
            <h2 className={styles.panelTitle} id="kpi-canal-heading">
              {channel === 'meta' ? 'Indicadores Meta Conversions API (CAPI)' : 'Indicadores GA4 Measurement Protocol (MP)'}
            </h2>
            <p className={styles.panelSubtitle}>
              {channel === 'meta'
                ? 'Métricas exclusivas do envio server-side para a Meta. O tráfego na landing até as compras aparece no funil abaixo.'
                : 'Métricas exclusivas do envio server-side para o Google Analytics 4. O tráfego na landing até as compras aparece no funil abaixo.'}
            </p>
          </div>
          <div className={styles.kpiGrid} aria-labelledby="kpi-canal-heading">
            {channel === 'meta'
              ? metaCapiKpis.map((item) => (
                  <article key={item.label} className={styles.kpiCard}>
                    <div className={styles.kpiHeader}>
                      <div>
                        <p className={styles.kpiLabel}>{item.label}</p>
                        <p className={styles.kpiValue}>{item.value}</p>
                      </div>
                      <span className={styles.kpiIcon}>
                        <Icon icon={item.icon} />
                      </span>
                    </div>
                    <p className={styles.kpiSubtext}>{item.subtext}</p>
                  </article>
                ))
              : ga4MpKpis.map((item) => (
                  <article key={item.label} className={styles.kpiCard}>
                    <div className={styles.kpiHeader}>
                      <div>
                        <p className={styles.kpiLabel}>{item.label}</p>
                        <p className={styles.kpiValue}>{item.value}</p>
                      </div>
                      <span className={styles.kpiIcon}>
                        <Icon icon={item.icon} />
                      </span>
                    </div>
                    <p className={styles.kpiSubtext}>{item.subtext}</p>
                  </article>
                ))}
          </div>

          {channel === 'meta' && metaData ? (
            <div className={styles.panelsGrid}>
              <section className={styles.panel}>
                <div className={styles.panelHeader}>
                  <div>
                    <h2 className={styles.panelTitle}>Funil na landing até a Meta (CAPI)</h2>
                    <p className={styles.panelSubtitle}>
                      As quatro primeiras etapas vêm do rastreamento interno da sua página. A etapa final conta compras com envio confirmado pela Conversions API da Meta (servidor). Requer Pixel e token de acesso válidos na campanha.
                    </p>
                  </div>
                </div>
                <div className={styles.funnelList}>
                  {metaData.funnel.map((step) => renderFunnelRow(step, funnelBaseMeta))}
                </div>
              </section>

              <section className={styles.panel}>
                <div className={styles.panelHeader}>
                  <div>
                    <h2 className={styles.panelTitle}>Entrega CAPI (Meta)</h2>
                    <p className={styles.panelSubtitle}>
                      {formatCompactDate(metaData.period.date_from)} – {formatCompactDate(metaData.period.date_to)}
                    </p>
                  </div>
                </div>

                <div className={styles.summaryGrid}>
                  <article className={styles.summaryCard}>
                    <p className={styles.summaryLabel}>Eventos tentados</p>
                    <p className={styles.summaryValue}>{formatNumber(metaData.delivery.attempted_events)}</p>
                    <p className={styles.summaryHint}>
                      Taxa de envio sobre compras: {formatPercent(metaData.delivery.send_rate_percent)}
                    </p>
                  </article>
                  <article className={styles.summaryCard}>
                    <p className={styles.summaryLabel}>Sucesso</p>
                    <p className={styles.summaryValue}>{formatNumber(metaData.delivery.sent_events)}</p>
                    <p className={styles.summaryHint}>
                      Taxa de sucesso: {formatPercent(metaData.delivery.success_rate_percent)}
                    </p>
                  </article>
                  <article className={styles.summaryCard}>
                    <p className={styles.summaryLabel}>Falha</p>
                    <p className={styles.summaryValue}>{formatNumber(metaData.delivery.failed_events)}</p>
                    <p className={styles.summaryHint}>
                      Taxa de falha: {formatPercent(metaData.delivery.failure_rate_percent)}
                    </p>
                  </article>
                  <article className={styles.summaryCard}>
                    <p className={styles.summaryLabel}>Ignorados</p>
                    <p className={styles.summaryValue}>{formatNumber(metaData.delivery.skipped_events)}</p>
                    <p className={styles.summaryHint}>Regras ou credenciais ausentes</p>
                  </article>
                </div>
              </section>
            </div>
          ) : null}

          {channel === 'ga4' && ga4Data ? (
            <div className={styles.panelsGrid} style={{ marginTop: 'var(--spacing-lg)' }}>
              <section className={styles.panel}>
                <div className={styles.panelHeader}>
                  <div>
                    <h2 className={styles.panelTitle}>Funil na landing até o GA4 (MP)</h2>
                    <p className={styles.panelSubtitle}>
                      As quatro primeiras etapas vêm do rastreamento interno da sua página. A etapa final conta compras com transmissão confirmada pelo Measurement Protocol do Google Analytics 4. Exige ID G e segredo MP configurados na campanha.
                    </p>
                  </div>
                </div>
                <div className={styles.funnelList}>
                  {ga4Data.funnel.map((step) => renderFunnelRow(step, funnelBaseGa))}
                </div>
              </section>

              <section className={styles.panel}>
                <div className={styles.panelHeader}>
                  <div>
                    <h2 className={styles.panelTitle}>Entrega GA4 (MP)</h2>
                    <p className={styles.panelSubtitle}>
                      {formatCompactDate(ga4Data.period.date_from)} – {formatCompactDate(ga4Data.period.date_to)}
                    </p>
                  </div>
                </div>

                <div className={styles.summaryGrid}>
                  <article className={styles.summaryCard}>
                    <p className={styles.summaryLabel}>Eventos tentados</p>
                    <p className={styles.summaryValue}>{formatNumber(ga4Data.delivery.attempted_events)}</p>
                    <p className={styles.summaryHint}>
                      Taxa de envio sobre compras: {formatPercent(ga4Data.delivery.send_rate_percent)}
                    </p>
                  </article>
                  <article className={styles.summaryCard}>
                    <p className={styles.summaryLabel}>Sucesso</p>
                    <p className={styles.summaryValue}>{formatNumber(ga4Data.delivery.sent_events)}</p>
                    <p className={styles.summaryHint}>
                      Taxa de sucesso: {formatPercent(ga4Data.delivery.success_rate_percent)}
                    </p>
                  </article>
                  <article className={styles.summaryCard}>
                    <p className={styles.summaryLabel}>Falha</p>
                    <p className={styles.summaryValue}>{formatNumber(ga4Data.delivery.failed_events)}</p>
                    <p className={styles.summaryHint}>
                      Taxa de falha: {formatPercent(ga4Data.delivery.failure_rate_percent)}
                    </p>
                  </article>
                  <article className={styles.summaryCard}>
                    <p className={styles.summaryLabel}>Ignorados</p>
                    <p className={styles.summaryValue}>{formatNumber(ga4Data.delivery.skipped_events)}</p>
                    <p className={styles.summaryHint}>Falta de ID G-, segredo MP ou filtros GA4</p>
                  </article>
                </div>
              </section>
            </div>
          ) : null}

          <section className={styles.panel} style={{ marginTop: 'var(--spacing-lg)' }}>
            <div className={styles.panelHeader}>
              <div>
                <h2 className={styles.panelTitle}>Volume por dia</h2>
                <p className={styles.panelSubtitle}>
                  {channel === 'meta'
                    ? 'Linhas de visualizações, checkouts e compras vêm do rastreamento interno da landing. Linhas verde e vermelha são somente envios Meta CAPI (servidor).'
                    : 'Linhas de visualizações, checkouts e compras vêm do rastreamento interno da landing. Linhas azul e laranja são somente transmissões GA4 MP (servidor).'}
                </p>
              </div>
            </div>
            <div className={styles.chartArea}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartPoints}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(15, 23, 42, 0.08)" />
                  <XAxis dataKey="date" tickFormatter={formatChartLabel} />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="page_views" name="Visualizações (landing)" stroke="#334155" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="checkouts" name="Checkouts (landing)" stroke="#7c3aed" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="purchases" name="Compras (landing)" stroke="#0f766e" strokeWidth={2} dot={false} />
                  {channel === 'meta' ? (
                    <>
                      <Line type="monotone" dataKey="capi_sent" name="CAPI Meta enviado" stroke="#16a34a" strokeWidth={2} dot={false} />
                      <Line type="monotone" dataKey="capi_failed" name="Falhas Meta CAPI" stroke="#dc2626" strokeWidth={2} dot={false} />
                    </>
                  ) : (
                    <>
                      <Line type="monotone" dataKey="mp_sent" name="GA4 MP transmitido" stroke="#0ea5e9" strokeWidth={2} dot={false} />
                      <Line type="monotone" dataKey="mp_failed" name="Falhas GA4 MP" stroke="#f97316" strokeWidth={2} dot={false} />
                    </>
                  )}
                </LineChart>
              </ResponsiveContainer>
            </div>
          </section>

          <div className={styles.panelsGrid} style={{ marginTop: 'var(--spacing-lg)', gridTemplateColumns: 'minmax(0, 1fr)' }}>
            <section className={styles.panel}>
              <h3 className={styles.panelTitle}>
                {channel === 'meta'
                  ? 'Alinhar com o Meta Events Manager e campanhas'
                  : 'Alinhar com o GA4, transmissão de dados e Google Ads'}
              </h3>
              {channel === 'meta' ? (
                <ul className={styles.panelSubtitle} style={{ paddingLeft: '1.25rem', lineHeight: 1.5 }}>
                  <li>
                    Confirme Pixel ID e token de acesso da campanha no Meta Events Manager antes de interpretar falhas de CAPI.
                  </li>
                  <li>
                    Use as taxas deste painel (servidor) como referência principal; o Pixel da Meta no navegador pode divergir por bloqueios de anúncio, extensões e consentimento do visitante.
                  </li>
                  <li>
                    Depois de validar envios aqui, crie ou ajuste conversões customizadas no Gerenciador de Anúncios com base em eventos server-side.
                  </li>
                </ul>
              ) : (
                <ul className={styles.panelSubtitle} style={{ paddingLeft: '1.25rem', lineHeight: 1.5 }}>
                  <li>Abra Administrador {'>'} Transmitir dados no GA4 para gerenciar secrets do Measurement Protocol e validar payloads no relatório Tempo Real.</li>
                  <li>Mantenha o navegador e o servidor alinhados: use o mesmo ID G configurado nos campos da campanha e evite repetir página com GTM mais gtag manual.</li>
                  <li>
                    Envie <code>gclid</code> clicando seu anúncio de testes e confirme nos eventos gravados pela
                    landing — o servidor repassa esse identificador ao MP quando existe no snapshot PIX.
                  </li>
                  <li>DebugView só recebe fluxos vindos dos SDK ou tags do navegador; eventos apenas por MP aparecem em relatórios padrão dentro de até 24–48&nbsp;h conforme política Google.</li>
                  <li>Configure conversões no Google Ads e em integrações do ecossistema Google somente após checar as taxas deste painel do Measurement Protocol, sem confiar apenas em valores do lado cliente.</li>
                </ul>
              )}
            </section>
          </div>

          {channel === 'meta' && metaData ? (
            <div className={styles.utmGrid}>
              {renderUtmTable(
                'UTM source',
                metaData.utms.sources,
                'Origem das compras na landing e envios bem-sucedidos à Meta.',
                'CAPI enviado'
              )}
              {renderUtmTable(
                'UTM campaign',
                metaData.utms.campaigns,
                'Campanha das compras na landing e envios bem-sucedidos à Meta.',
                'CAPI enviado'
              )}
              {renderUtmTable(
                'UTM medium',
                metaData.utms.mediums,
                'Mídia das compras na landing e envios bem-sucedidos à Meta.',
                'CAPI enviado'
              )}
            </div>
          ) : null}

          {channel === 'ga4' && ga4Data ? (
            <div className={styles.utmGrid}>
              {renderUtmTable(
                'UTM source',
                ga4Data.utms.sources,
                'Origem das compras na landing e transmissões bem-sucedidas ao GA4.',
                'MP transmitido'
              )}
              {renderUtmTable(
                'UTM campaign',
                ga4Data.utms.campaigns,
                'Campanha das compras na landing e transmissões bem-sucedidas ao GA4.',
                'MP transmitido'
              )}
              {renderUtmTable(
                'UTM medium',
                ga4Data.utms.mediums,
                'Mídia das compras na landing e transmissões bem-sucedidas ao GA4.',
                'MP transmitido'
              )}
            </div>
          ) : null}
        </>
      )}
    </div>
  )
}
